// src/pages/CuentasPorPagar.js
// Módulo semi-oculto: deudas pendientes con proveedores
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import Protegido from "../components/Protegido";
import "../estilos/CrearDocumentoEstilo.css";

// ─── Helpers ───────────────────────────────────────────────────
const soloFecha = (valor) => {
  if (!valor) return "—";
  const s = String(valor).trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

// ═══════════════════════════════════════════════════════════════
export default function CuentasPorPagar() {
  const navigate = useNavigate();
  const [deudas, setDeudas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  // ─── Carga inicial ──────────────────────────────────────────
  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      const { data, error } = await supabase
        .from("ordenes_pedido")
        .select("*, clientes(*)")
        .eq("cerrada", false)
        .order("fecha_evento", { ascending: true });

      if (error) {
        console.error("Error cargando órdenes:", error);
        setCargando(false);
        return;
      }

      // Extraer pagos a proveedores con saldo pendiente
      const pendientes = [];
      (data || []).forEach((o) => {
        const pagos = o.pagos_proveedores;
        if (!Array.isArray(pagos) || pagos.length === 0) return;
        pagos.forEach((pp) => {
          const totalCompra = Number(pp.total || pp.subtotal_compra || 0);
          if (totalCompra <= 0) return;
          const abonado = (pp.abonos || []).reduce((s, a) => s + Number(a.valor || 0), 0);
          const saldo = totalCompra - abonado;
          if (saldo > 0) {
            pendientes.push({
              orden: o,
              orden_numero: o.numero || "OP-???",
              fecha_evento: o.fecha_evento,
              cliente_nombre: o.clientes?.nombre || "—",
              proveedor_nombre: pp.proveedor_nombre || pp.nombre || "Proveedor",
              total_compra: totalCompra,
              abonado,
              saldo,
              abonos: pp.abonos || [],
            });
          }
        });
      });

      // Ordenar por mayor saldo primero
      pendientes.sort((a, b) => b.saldo - a.saldo);
      setDeudas(pendientes);
      setCargando(false);
    };
    cargar();
  }, []);

  // ─── Filtro ──────────────────────────────────────────────────
  const deudasFiltradas = useMemo(() => {
    if (!busqueda.trim()) return deudas;
    const q = busqueda.toLowerCase();
    return deudas.filter(
      (d) =>
        d.proveedor_nombre.toLowerCase().includes(q) ||
        d.orden_numero.toLowerCase().includes(q) ||
        d.cliente_nombre.toLowerCase().includes(q)
    );
  }, [deudas, busqueda]);

  // ─── KPIs ────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalDeuda = deudasFiltradas.reduce((s, d) => s + d.saldo, 0);
    const totalAbonado = deudasFiltradas.reduce((s, d) => s + d.abonado, 0);
    const proveedores = [...new Set(deudasFiltradas.map((d) => d.proveedor_nombre))];
    const mayorDeuda = proveedores.length > 0
      ? proveedores.map((p) => ({
          nombre: p,
          total: deudasFiltradas.filter((d) => d.proveedor_nombre === p).reduce((s, d) => s + d.saldo, 0),
        })).sort((a, b) => b.total - a.total)[0]
      : null;
    return { totalDeuda, totalAbonado, proveedoresCount: proveedores.length, mayorDeuda };
  }, [deudasFiltradas]);

  // ─── Editar orden ────────────────────────────────────────────
  const editarOrden = (orden) => {
    const cliente = orden.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...orden,
          nombre_cliente: cliente.nombre || "",
          identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "",
          direccion: cliente.direccion || "",
          email: cliente.email || "",
          fecha_creacion: orden.fecha_creacion || orden.fecha || null,
          abonos: orden.abonos || [],
          garantia: orden.garantia || "",
          garantiaRecibida: orden.garantiaRecibida || false,
          estado: orden.estado || "",
          numero: orden.numero || "",
          esEdicion: true,
          idOriginal: orden.id,
        },
        tipo: "orden",
      },
    });
  };

  // ─── Agrupar por proveedor ───────────────────────────────────
  const agrupado = useMemo(() => {
    const mapa = new Map();
    deudasFiltradas.forEach((d) => {
      if (!mapa.has(d.proveedor_nombre)) {
        mapa.set(d.proveedor_nombre, { nombre: d.proveedor_nombre, items: [], totalSaldo: 0, totalAbonado: 0 });
      }
      const grupo = mapa.get(d.proveedor_nombre);
      grupo.items.push(d);
      grupo.totalSaldo += d.saldo;
      grupo.totalAbonado += d.abonado;
    });
    return [...mapa.values()].sort((a, b) => b.totalSaldo - a.totalSaldo);
  }, [deudasFiltradas]);

  // ─── Render ──────────────────────────────────────────────────
  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 960 }}>

          {/* HEADER */}
          <div className="sw-header">
            <h1 className="sw-header-titulo">🏢 Cuentas por Pagar</h1>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
              Deudas pendientes con tus proveedores
            </p>
          </div>

          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Deuda total", valor: money(kpis.totalDeuda), color: "#d97706", bg: "#fffbeb", tip: "Suma total de lo que le debes a tus proveedores" },
              { label: "Ya abonado", valor: money(kpis.totalAbonado), color: "#059669", bg: "#ecfdf5", tip: "Total que ya has pagado a proveedores en estos pedidos" },
              { label: "Proveedores", valor: kpis.proveedoresCount, color: "#7c3aed", bg: "#f5f3ff", tip: "Cantidad de proveedores a los que les debes" },
              { label: "Mayor deuda", valor: kpis.mayorDeuda ? kpis.mayorDeuda.nombre : "—", color: "#dc2626", bg: "#fef2f2", tip: kpis.mayorDeuda ? `Le debes ${money(kpis.mayorDeuda.total)} a este proveedor` : "No hay deudas" },
            ].map((kpi, i) => (
              <div key={i} title={kpi.tip} style={{
                padding: "14px 16px",
                borderRadius: 10,
                background: kpi.bg,
                border: "1px solid #e5e7eb",
                textAlign: "center",
                cursor: "help",
                transition: "transform 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4, textTransform: "uppercase" }}>{kpi.label}</div>
                <div style={{
                  fontSize: kpi.label === "Mayor deuda" ? 14 : 20,
                  fontWeight: 700,
                  color: kpi.color,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>{kpi.valor}</div>
              </div>
            ))}
          </div>

          {/* FILTRO */}
          <div className="cd-card" style={{ marginBottom: 16 }}>
            <div className="cd-card-body">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                <div style={{ flex: "1 1 250px" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>🔍 Buscar</label>
                  <input
                    type="text"
                    placeholder="Proveedor, pedido o cliente..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                  />
                </div>
                {busqueda && (
                  <button onClick={() => setBusqueda("")} className="cd-btn cd-btn-gris" style={{ fontSize: 12 }}>
                    ✕ Limpiar
                  </button>
                )}
                <button onClick={() => navigate("/proveedores")} className="cd-btn cd-btn-azul" style={{ fontSize: 13, marginLeft: "auto" }}>
                  🏢 Ir a Proveedores
                </button>
              </div>
            </div>
          </div>

          {/* CONTADOR */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 14px",
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
            color: "#92400e",
            fontWeight: 500,
          }}>
            <span>{deudasFiltradas.length} deuda{deudasFiltradas.length !== 1 ? "s" : ""} en {kpis.proveedoresCount} proveedor{kpis.proveedoresCount !== 1 ? "es" : ""}</span>
            <span style={{ fontWeight: 700 }}>Total: {money(kpis.totalDeuda)}</span>
          </div>

          {/* VACÍO */}
          {!cargando && deudas.length === 0 && (
            <div style={{
              textAlign: "center",
              padding: "48px 24px",
              color: "#9ca3af",
              background: "white",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
              <div style={{ fontSize: 15, fontWeight: 500 }}>¡Todo al día!</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>No tienes deudas pendientes con proveedores</div>
            </div>
          )}

          {/* LISTA AGRUPADA POR PROVEEDOR */}
          {agrupado.map((grupo) => (
            <div key={grupo.nombre} style={{ marginBottom: 16 }}>
              {/* Header del proveedor */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 16px",
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                borderRadius: "10px 10px 0 0",
                color: "white",
                flexWrap: "wrap",
                gap: 8,
              }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>🏢 {grupo.nombre}</span>
                <div style={{ display: "flex", gap: 12, fontSize: 13 }}>
                  <span>Abonado: {money(grupo.totalAbonado)}</span>
                  <span style={{ fontWeight: 700 }}>Debes: {money(grupo.totalSaldo)}</span>
                </div>
              </div>

              {/* Items del proveedor */}
              {grupo.items.map((d, idx) => (
                <div
                  key={`${d.orden.id}-${d.proveedor_nombre}-${idx}`}
                  style={{
                    background: "white",
                    border: "1px solid #fde68a",
                    borderTop: idx === 0 ? "none" : "1px solid #fde68a",
                    padding: "12px 16px",
                    borderRadius: idx === grupo.items.length - 1 ? "0 0 10px 10px" : 0,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    {/* Info izquierda */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#d97706",
                          background: "rgba(217,119,6,0.1)",
                          padding: "2px 8px",
                          borderRadius: 6,
                        }}>
                          {d.orden_numero}
                        </span>
                        <span style={{ fontSize: 13, color: "#374151" }}>
                          👤 {d.cliente_nombre}
                        </span>
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>
                          📅 {soloFecha(d.fecha_evento)}
                        </span>
                      </div>

                      {/* Barra de progreso de pago */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                        <div style={{ flex: 1, maxWidth: 200, height: 6, background: "#f3f4f6", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{
                            width: `${d.total_compra > 0 ? Math.round((d.abonado / d.total_compra) * 100) : 0}%`,
                            height: "100%",
                            background: d.abonado > 0 ? "linear-gradient(90deg, #34d399, #059669)" : "#e5e7eb",
                            borderRadius: 3,
                            transition: "width 0.3s",
                          }} />
                        </div>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>
                          {d.total_compra > 0 ? Math.round((d.abonado / d.total_compra) * 100) : 0}% pagado
                        </span>
                      </div>
                    </div>

                    {/* Montos */}
                    <div style={{ textAlign: "right", minWidth: 120 }}>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        Total: {money(d.total_compra)} · Abonado: {money(d.abonado)}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#dc2626", marginTop: 2 }}>
                        {money(d.saldo)}
                      </div>
                    </div>

                    {/* Botón editar */}
                    <button
                      onClick={() => editarOrden(d.orden)}
                      title="Editar pedido y registrar abono"
                      style={{
                        padding: "7px 12px",
                        background: "rgba(217,119,6,0.08)",
                        border: "1px solid rgba(217,119,6,0.25)",
                        borderRadius: 7,
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#92400e",
                        fontWeight: 500,
                      }}
                    >
                      ✏️ Abonar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {cargando && (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
              ⏳ Cargando deudas...
            </div>
          )}

        </div>
      </div>
    </Protegido>
  );
}